import { useQueryClient } from "@tanstack/react-query";
import React from "react";

import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { QuestionTypeForm as EditComponent } from "./questionTypeForm";

import { QuestionTypeView as QuestionTypeViewComponenet } from "./questionTypeView";
import { QuestionTypePage } from "./questionTypePage";
import { QuestionTypeService } from "./questionTypeService";
import { LookupQuestionTypeDto } from "../../../services/dto";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const QuestionTypeContainer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [data, setData] = React.useState<LookupQuestionTypeDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const questiontypeQuery = QuestionTypeService.getQuestionTypeQuery(
    data?.id ?? undefined
  );

  const { status: saveStatus, mutateAsync: saveQuestiontype } = QuestionTypeService.save();
  const { status: deActivateStatus, mutate: deleteMutate } =
    QuestionTypeService.deActivateQuestiontype();

  const { setSearchAction, clearSearchAction } = useActionFromQuery(OnCustomEvent);
  function EditRecord(data: any, showView: boolean | undefined) {
    setData(data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, data?.id);
  }

  function OnCustomEvent(actionType: string, dataId: string) {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as LookupQuestionTypeDto);
      setSearchAction(actionType, dataId);
    }
  }

  const handleSubmit = async (values: LookupQuestionTypeDto) => {
    const isEdit = data && data.id && data.id !== Constants.emptyGuid;
    values.id = isEdit ? data.id : Constants.emptyGuid;
    const message = isEdit ? "Questiontype updated" : "Questiontype saved";
    return await saveQuestiontype(values, {
      onSuccess: (response) => {
        const errorMessage = apiErrorMessage(response.errorMessages);
        if (errorMessage) {
          showSnackbar(errorMessage, MessageSeverity.Error);
        } else {
          showSnackbar(message, MessageSeverity.Success);
          questiontypeQuery.refetch();
          queryClient.invalidateQueries({
            queryKey: ["questiontypeGridQuery"],
          });
          closeDrawer();
        }
      },
    });
  };

  const closeDrawer = () => {
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const handleDelete = () => {
    deleteMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => {
        const errorMessage = apiErrorMessage(response.errorMessages);
        if (errorMessage) {
          showSnackbar(errorMessage, MessageSeverity.Error);
        } else {
          showSnackbar("Questiontype deleted", MessageSeverity.Success);
          questiontypeQuery.refetch();
          queryClient.invalidateQueries({
            queryKey: ["questiontypeGridQuery"],
          });
          closeDrawer();
        }
      },
    });
  };

  const renderDrawerContent = () => {
    const isDataLoaded = !!questiontypeQuery?.data;
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW: {
        return (
          <EditComponent
            show={true}
            onHide={() => closeDrawer()}
            onSave={(data: LookupQuestionTypeDto) => handleSubmit(data)}
          />
        );
      }

      case GRID_ACTION_TYPES.EDIT: {
        return isDataLoaded ? (
          <EditComponent
            data={questiontypeQuery?.data}
            show={!!questiontypeQuery?.data}
            onHide={() => closeDrawer()}
            onSave={(data: LookupQuestionTypeDto) => handleSubmit(data)}
          />
        ) : null;
      }
      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK: {
        return isDataLoaded ? (
          <QuestionTypeViewComponenet
            data={questiontypeQuery?.data}
            show={!!questiontypeQuery?.data}
            onHide={() => closeDrawer()}
            onDelete={handleDelete}
            onEdit={() => EditRecord(questiontypeQuery.data, true)}
          />
        ) : null;
      }
    }
  };

  return (
    <div>
      <QuestionTypePage onCustomEvent={OnCustomEvent} />
      {renderDrawerContent()}
    </div>
  );
};
